import { useState, useEffect } from 'react';
import { fetchAnalytics } from '../../api/relances.api';

const PERIODS = [
  { label: '7 jours', days: 7 },
  { label: '30 jours', days: 30 },
  { label: '90 jours', days: 90 },
];

const STATUS_COLORS = {
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  SENDING:   'bg-violet-100 text-violet-700',
  READY:     'bg-sky-100 text-sky-700',
  DRAFT:     'bg-slate-100 text-slate-600',
  FAILED:    'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-50 text-red-500',
};
const STATUS_LABELS = {
  COMPLETED: 'Terminée', SENDING: 'En cours', READY: 'Planifiée',
  DRAFT: 'Brouillon', FAILED: 'Échouée', CANCELLED: 'Annulée',
};

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function RelancesDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    loadData();
  }, [period, useCustom, customFrom, customTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const from = useCustom ? customFrom : daysAgo(period);
      const to = useCustom ? customTo : today();
      const result = await fetchAnalytics({ from, to });
      setData(result);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const summary = data?.summary || {};
  const campaigns = data?.campaigns || [];

  return (
    <div>
      {/* Header + Period */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Tableau de bord</h3>
          <p className="text-sm text-slate-500">Vue d'ensemble des campagnes de relance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.days}
              onClick={() => { setPeriod(p.days); setUseCustom(false); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                !useCustom && period === p.days
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={customFrom}
              onChange={e => { setCustomFrom(e.target.value); setUseCustom(true); }}
              className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white text-sm"
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              value={customTo}
              onChange={e => { setCustomTo(e.target.value); setUseCustom(true); }}
              className="px-2 py-1.5 border border-slate-200 rounded-lg bg-white text-sm"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : (
        <>
          {/* KPI Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">Campagnes</div>
              <div className="text-3xl font-bold text-slate-900">{summary.total_campaigns || 0}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">Emails envoyés</div>
              <div className="text-3xl font-bold text-emerald-600">{summary.total_sent || 0}</div>
              {summary.total_failed > 0 && (
                <div className="text-xs text-red-500 mt-1">{summary.total_failed} échecs</div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">Taux de succès</div>
              <div className={`text-3xl font-bold ${
                summary.send_rate_pct >= 80 ? 'text-emerald-600'
                : summary.send_rate_pct >= 50 ? 'text-yellow-600'
                : 'text-red-600'
              }`}>
                {summary.send_rate_pct || 0}%
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">Canal Email</div>
              <div className="text-3xl font-bold text-slate-900">{summary.email_pct || 0}%</div>
              <div className="text-xs text-slate-500 mt-1">📧 {summary.total_email || 0} / 📱 {summary.total_wa || 0}</div>
            </div>
          </div>

          {/* Recent campaigns */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-xs font-semibold text-slate-500">Campagnes récentes ({campaigns.length})</span>
            </div>
            {campaigns.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">Aucune campagne sur la période</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-4 font-medium text-slate-500">Campagne</th>
                      <th className="text-center py-2 px-4 font-medium text-slate-500">Statut</th>
                      <th className="text-center py-2 px-4 font-medium text-slate-500">Destinataires</th>
                      <th className="text-center py-2 px-4 font-medium text-slate-500">Envoyés</th>
                      <th className="text-center py-2 px-4 font-medium text-slate-500">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.slice(0, 10).map(c => {
                      const total = Number(c.total_recipients) || 0;
                      const sent = Number(c.sent_count) || 0;
                      const rate = total > 0 ? Math.round(sent / total * 100) : null;
                      const statusClass = STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-600';
                      return (
                        <tr key={c.id} className="border-b border-slate-100">
                          <td className="py-2.5 px-4 font-medium text-slate-900">{c.name}</td>
                          <td className="py-2.5 px-4 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                              {STATUS_LABELS[c.status] || c.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-center text-slate-700">{total}</td>
                          <td className="py-2.5 px-4 text-center text-emerald-600 font-medium">{sent}</td>
                          <td className="py-2.5 px-4 text-center">
                            {rate !== null ? (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                rate >= 80 ? 'bg-emerald-100 text-emerald-700'
                                : rate >= 50 ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                              }`}>{rate}%</span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
